import type { SiteManifest } from '../core/SiteManifest';

/**
 * 導入画面(計画書F-7, Phase 7)。
 * 権限の事前説明 → 安全注意への同意 → タップで体験開始。
 * onStart はタップハンドラ内で同期的に呼ばれる(iOSの権限・音声制限のため、
 * DeviceOrientation権限取得とAudioContext.resume()はこのコールバック内で行うこと)。
 */
export interface AnchorChoice {
  /** チェックボックスのラベル文言 */
  label: string;
  /** 初期チェック状態(チェック時は史跡の実座標にアンカー) */
  defaultChecked: boolean;
}

export class IntroScreen {
  private root: HTMLElement;
  private statusEl: HTMLElement;
  private errorEl: HTMLElement;
  private startBtn: HTMLButtonElement;

  constructor(
    container: HTMLElement,
    manifest: SiteManifest,
    pathLabel: string,
    anchorChoice: AnchorChoice | null,
    onStart: (anchorFixed: boolean) => Promise<void>,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'intro';
    this.root.innerHTML = `
      <h1>ShisekiAR</h1>
      <div class="site-title"></div>
      <p class="description"></p>
      <div class="permissions">
        体験には以下の許可が必要です。開始ボタンを押すと順に確認が表示されます:<br>
        ・位置情報(史跡との位置関係の計算に使用)<br>
        ・モーションと画面の向き(方位の取得に使用)<br>
        ・カメラ(現実の風景への重畳表示に使用)<br>
        取得した情報は端末内でのみ使用され、外部に送信されません。
      </div>
      <div class="safety">
        <strong>⚠ 安全のためのお願い</strong><br>
        ・歩きながらのスマートフォン操作はおやめください<br>
        ・車道・線路・立入禁止区域には絶対に入らないでください<br>
        ・周囲の歩行者・自転車・車に十分ご注意ください<br>
        ・体験は安全な場所に立ち止まって行ってください
      </div>
      ${
        anchorChoice
          ? `<label class="option">
        <input type="checkbox" id="anchor-check" ${anchorChoice.defaultChecked ? 'checked' : ''} />
        <span></span>
      </label>`
          : ''
      }
      <label class="consent">
        <input type="checkbox" id="consent-check" />
        上記の注意事項を読み、同意します
      </label>
      <button class="btn-primary" id="start-btn" disabled>体験を開始する</button>
      <div class="status"></div>
      <div class="error"></div>
      <div class="permissions" style="opacity:0.5">表示モード: ${pathLabel}</div>
    `;
    (this.root.querySelector('.site-title') as HTMLElement).textContent = manifest.title;
    (this.root.querySelector('.description') as HTMLElement).textContent =
      manifest.description || 'この場所で過去に起きた出来事を、その場でAR疑似体験できます。';
    if (anchorChoice) {
      (this.root.querySelector('.option span') as HTMLElement).textContent = anchorChoice.label;
    }

    this.statusEl = this.root.querySelector('.status') as HTMLElement;
    this.errorEl = this.root.querySelector('.error') as HTMLElement;
    this.startBtn = this.root.querySelector('#start-btn') as HTMLButtonElement;
    const consent = this.root.querySelector('#consent-check') as HTMLInputElement;

    consent.addEventListener('change', () => {
      this.startBtn.disabled = !consent.checked;
    });

    this.startBtn.addEventListener('click', () => {
      this.errorEl.textContent = '';
      this.startBtn.disabled = true;
      this.setStatus('準備中…');
      const anchorEl = this.root.querySelector('#anchor-check') as HTMLInputElement | null;
      // await せずタップハンドラのコールスタック内で開始する(iOS制限対応)
      onStart(anchorEl?.checked ?? true)
        .then(() => this.hide())
        .catch((e: unknown) => {
          console.error(e);
          this.showError(e instanceof Error ? e.message : String(e));
          this.startBtn.disabled = false;
          this.setStatus('');
        });
    });

    container.appendChild(this.root);
  }

  setStatus(text: string): void {
    this.statusEl.textContent = text;
  }

  showError(text: string): void {
    this.errorEl.textContent = `エラー: ${text}`;
  }

  hide(): void {
    this.root.remove();
  }
}
